// src/components/ClaimRealityCard.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, AlertCircle, ChevronDown, ChevronUp, Share2, Copy, ExternalLink, Check } from 'lucide-react';
import CardContainer from './CardContainer';

// SpeakerAvatar component to handle avatar display consistently
const SpeakerAvatar = ({ source, isMobile }) => {
  // Extract speaker name from source string
  const fullSource = source;
  const speaker = fullSource.split(/,|\s+via\s+/)[0].trim();
  
  // Create clean ID for file paths
  const speakerId = speaker.toLowerCase().replace(/\s+/g, '-');
  
  // Generate initials for fallback
  const initials = speaker.split(' ')
    .map(name => name?.[0] || '')
    .join('')
    .toUpperCase();
  
  // State to track if image is loaded
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Test loading the image when component mounts
  useEffect(() => {
    // For debugging
    console.log(`Avatar: trying to load ${speaker} (${speakerId})`);
    
    const img = new Image();
    img.onload = () => {
      console.log(`Avatar: successfully loaded image for ${speaker}`);
      setImageLoaded(true);
    };
    img.onerror = () => {
      console.log(`Avatar: failed to load image for ${speaker}`);
      setImageLoaded(false);
    };
    img.src = `/images/speakers/${speakerId}.jpg`;
    
    // Return cleanup function
    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [speaker, speakerId]);
  
  return (
    <div className={`w-full h-full flex items-center justify-center ${!imageLoaded ? 'bg-blue-200' : ''}`}>
      {imageLoaded ? (
        <img 
          src={`/images/speakers/${speakerId}.jpg`}
          alt={speaker}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className={`text-blue-800 font-bold ${isMobile ? 'text-sm' : 'text-base'}`}>
          {initials}
        </span>
      )}
    </div>
  );
};

// Format date to MM/DD/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
};

const ClaimRealityCard = ({ quote, defaultExpanded = false, standalone = true, onToggleAnalysis, analysisExpanded }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [sourcesExpanded, setSourcesExpanded] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [realityExpanded, setRealityExpanded] = useState(false);
  const [desktopRealityExpanded, setDesktopRealityExpanded] = useState(false);
  
  // Check if screen is mobile size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    
    // Initial check
    checkMobile();
    
    // Add event listener for resize
    window.addEventListener('resize', checkMobile);
    
    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Parse reality sources if they exist
  const realitySources = quote.reality_sources ? 
    (typeof quote.reality_sources === 'string' ? 
      JSON.parse(quote.reality_sources) : quote.reality_sources) : [];
  
  // Professional colors
  const colors = {
    badgeText: "text-slate-700",
    badgeBg: "bg-slate-200",
    claimIconBg: "bg-blue-100",
    claimIconText: "text-blue-600",
    realityIconBg: "bg-slate-200",
    realityIconText: "text-slate-700",
    buttonBg: "bg-slate-100 hover:bg-slate-200",
    buttonText: "text-slate-700",
    linkText: "text-slate-600 hover:text-slate-800",
    borderColor: "border-slate-200",
    checkIcon: "text-slate-700"
  };
  
  // Get veracity color in slate theme
  const getVeracityColor = (rating) => {
    switch(rating?.toLowerCase()) {
      case 'false': return 'bg-red-100 text-red-800';
      case 'misleading': return 'bg-orange-100 text-orange-800';
      case 'partly true': return 'bg-yellow-100 text-yellow-800';
      case 'mostly true': return 'bg-slate-200 text-slate-800';
      case 'true': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };
  
  // Handle copy to clipboard
  const handleCopyLink = () => {
    // Generate a slug from the quote text
const generateSlug = (text) => {
  if (!text) return 'quote';
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .substring(0, 60)
    .replace(/-+$/, ''); // Remove trailing dashes
};

const quoteSlug = generateSlug(quote.claim_text || quote.text);
const url = `${window.location.origin}/q/${quote.id}/${quoteSlug}.html`;
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      })
      .catch(err => console.error('Failed to copy link: ', err));
  };
  
  // YouTube video embed handling
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    
    return (match && match[2].length === 11)
      ? `https://www.youtube.com/embed/${match[2]}`
      : null;
  };
  
  // Handle both naming conventions (video_url and videoUrl)
  const videoUrl = quote.video_url || quote.videoUrl;
  
  const embedUrl = videoUrl ? getYouTubeEmbedUrl(videoUrl) : null;
  const hasAdditionalContent = embedUrl; // Citation now appears in source line
  
  // Make sure YouTube video URL is stored in a consistent location for sharing
  useEffect(() => {
    if (videoUrl && !quote.video_url) {
      quote.video_url = videoUrl;
    }
  }, [videoUrl, quote]);

  // Empty header since date will be moved into claim area
  const header = null;

  // Footer component
  const footer = standalone ? (
    <div className={`flex ${isMobile ? 'flex-col space-y-2' : 'justify-between'} items-center`}>
      <div className={`flex ${isMobile ? 'self-start' : 'space-x-2'}`}>
        {hasAdditionalContent && (
          <button
            onClick={() => setExpanded(!expanded)}
            className={`flex items-center ${colors.linkText} transition ${isMobile ? 'text-xs' : 'text-sm'}`}
          >
            {expanded ? (
              <>
                <ChevronUp size={isMobile ? 14 : 16} className="mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown size={isMobile ? 14 : 16} className="mr-1" />
                Show more
              </>
            )}
          </button>
        )}
        
        <Link
          to={`/q/${quote.id}.html`}
          className={`flex items-center ${colors.linkText} transition ${isMobile ? 'text-xs ml-2' : 'text-sm ml-3'}`}
        >
          <Share2 size={isMobile ? 14 : 16} className="mr-1" />
          Permalink
        </Link>
      </div>
      
      <button 
        onClick={handleCopyLink}
        className={`flex items-center px-3 py-1 ${isMobile ? 'text-xs mt-2' : 'text-sm'} ${colors.buttonText} ${colors.buttonBg} rounded-md ${isMobile ? 'self-end' : ''}`}
      >
        <Copy size={isMobile ? 12 : 14} className="mr-1" />
        {copySuccess ? 'Copied!' : 'Copy Link'}
      </button>
    </div>
  ) : null;

  // Main content
  const content = (
    <>
      {/* Claim Section */}
      <div className={`${isMobile ? 'px-4 py-3' : 'px-6 py-4'} border-b border-gray-200`}>
        <div className="flex space-x-4">
          <div className="flex-shrink-0">
            {/* Avatar image replaces the message icon - further increased size */}
            <div className={`${isMobile ? 'w-10 h-10' : 'w-14 h-14'} rounded-full overflow-hidden border-2 border-white shadow-sm flex items-center justify-center ${colors.claimIconBg}`}>
              {/* More reliable approach to speaker avatars with initials fallback */}
              <SpeakerAvatar 
                source={quote.claim_source || quote.source || ''} 
                isMobile={isMobile} 
              />
            </div>
          </div>
          <div className="flex-grow">
            <div className="flex justify-between items-center mb-2">
              <h3 className="flex items-center text-md font-semibold text-gray-900">
                <span className="border-b-2 border-blue-500 pb-1">Claim</span>
                {quote.veracity_rating && (
                  <span className={`ml-2 px-2 py-0.5 ${getVeracityColor(quote.veracity_rating)} text-xs font-medium rounded-full`}>
                    {quote.veracity_rating}
                  </span>
                )}
                {quote.truthfulness_score !== undefined && quote.truthfulness_score !== null && (
                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                    Score: {quote.truthfulness_score}/10
                  </span>
                )}
              </h3>
              <div className={`${isMobile ? 'text-xs' : 'text-sm'} text-gray-500`}>
                {formatDate(quote.claim_date || quote.date)}
              </div>
            </div>
            {/* Quote bubble with modified styling - adjusted for top avatar */}
            <blockquote className={`relative text-gray-800 px-4 py-3 mt-4 mb-2 italic bg-blue-50 rounded-lg ${isMobile ? 'text-sm leading-relaxed' : 'text-base leading-relaxed font-medium'}`}>
              {/* Speech bubble tail - positioned at top left for the avatar, adjusted for larger avatar */}
              <div className="absolute w-4 h-4 bg-blue-50 transform rotate-45" style={{ top: '-8px', left: isMobile ? '24px' : '30px' }}></div>
              
              <span className="text-gray-400 font-serif text-xl mr-1">"</span>
              {quote.claim_text || quote.text}
              <span className="text-gray-400 font-serif text-xl ml-1">"</span>
            </blockquote>
            
            {/* Speaker attribution section */}
            <div className="text-sm text-gray-600 mt-2">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <p className={isMobile ? 'text-xs' : ''}><strong>Source:</strong> {quote.claim_source || quote.source}</p>
                {quote.citation && (
                  <a
                    href={quote.citation}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${colors.linkText} hover:underline ${isMobile ? 'text-xs' : 'text-sm'} flex items-center`}
                  >
                    <ExternalLink size={isMobile ? 10 : 12} className="mr-1" /> 
                    Link to source
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reality Section */}
      <div className={`${isMobile ? 'px-4 py-3' : 'px-6 py-4'} bg-gray-50`}>
        <div className="flex space-x-3">
          <div className="flex-shrink-0">
            <div className={`${isMobile ? 'w-7 h-7' : 'w-8 h-8'} ${colors.realityIconBg} rounded-full flex items-center justify-center`}>
              <AlertCircle size={isMobile ? 16 : 18} className={colors.realityIconText} />
            </div>
          </div>
          <div className="flex-grow">
            <h3 className="flex justify-between items-center text-md font-semibold text-gray-900 mb-2">
              <span className="border-b-2 border-slate-500 pb-1">Reality</span>
              {isMobile ? (
                <button 
                  onClick={() => setRealityExpanded(!realityExpanded)}
                  className={`flex items-center ${colors.linkText} text-xs font-medium`}
                  aria-expanded={realityExpanded}
                >
                  {realityExpanded ? (
                    <ChevronUp size={14} className="ml-1" />
                  ) : (
                    <ChevronDown size={14} className="ml-1" />
                  )}
                </button>
              ) : (
                <button 
                  onClick={() => setDesktopRealityExpanded(!desktopRealityExpanded)}
                  className={`flex items-center ${colors.linkText} text-sm font-medium`}
                  aria-expanded={desktopRealityExpanded}
                >
                  {desktopRealityExpanded ? (
                    <>
                      <ChevronUp size={16} className="mr-1" />
                      <span>Show Less</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown size={16} className="mr-1" />
                      <span>Read More</span>
                    </>
                  )}
                </button>
              )}
            </h3>
            
            {/* Reality text - collapsible with height reservation to prevent layout shifts */}
            <div className="text-gray-700">
              <div className="bg-slate-50 p-3 rounded border-l-4 border-slate-300 mb-3">
                {isMobile && !realityExpanded ? (
                  // Mobile collapsed view with height preservation
                  <div className="relative">
                    <div className="max-h-[7em] overflow-hidden">
                      <p className="text-sm leading-relaxed whitespace-pre-line">
                        {quote.reality_text || quote.text}
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-50 to-transparent"></div>
                  </div>
                ) : !isMobile && !desktopRealityExpanded ? (
                  // Desktop collapsed view with height preservation
                  <div className="relative">
                    <div className="max-h-[16em] overflow-hidden">
                      <p className="leading-relaxed whitespace-pre-line">
                        {quote.reality_text || quote.text}
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-slate-50 to-transparent"></div>
                  </div>
                ) : (
                  // Expanded view (both mobile and desktop)
                  <p className={`${isMobile ? 'text-sm leading-relaxed' : 'leading-relaxed'} whitespace-pre-line`}>
                    {quote.reality_text || quote.text}
                  </p>
                )}
              </div>
            </div>
            
            {/* Actions Bar for Reality Section */}
            <div className="mt-4 border-t border-gray-200 pt-3">
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {/* Analysis Button (Primary) - Only show if we have the callback and not standalone */}
                {!standalone && onToggleAnalysis && (
                  <button 
                    onClick={onToggleAnalysis} 
                    className={`flex items-center bg-blue-50 ${analysisExpanded ? 'text-blue-700' : 'text-blue-600'} ${isMobile ? 'text-xs' : 'text-sm'} font-medium hover:bg-blue-100 rounded px-3 py-1.5 border border-blue-200`}
                  >
                    {analysisExpanded ? (
                      <>
                        <ChevronUp size={isMobile ? 14 : 16} className="mr-1.5" />
                        Hide Analysis
                      </>
                    ) : (
                      <>
                        <ChevronDown size={isMobile ? 14 : 16} className="mr-1.5" />
                        Show Analysis
                      </>
                    )}
                  </button>
                )}
                
                {/* Sources Button (Secondary) - Only show if sources exist */}
                {(realitySources && realitySources.length > 0 || embedUrl) && (
                  <button 
                    onClick={() => setSourcesExpanded(!sourcesExpanded)} 
                    className={`flex items-center ${colors.linkText} ${isMobile ? 'text-xs' : 'text-sm'} font-medium hover:bg-gray-50 rounded px-3 py-1.5 border border-gray-200`}
                  >
                    {sourcesExpanded ? (
                      <>
                        <ChevronUp size={isMobile ? 14 : 16} className="mr-1.5" />
                        Hide Sources
                      </>
                    ) : (
                      <>
                        <ChevronDown size={isMobile ? 14 : 16} className="mr-1.5" />
                        View Sources
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
            
            {/* Expanded Sources Content */}
            {sourcesExpanded && (
              <div className="mt-4 space-y-4 bg-gray-50 p-4 rounded-md border border-gray-200">
                {/* Sources */}
                {realitySources && realitySources.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Sources</h4>
                    {realitySources.map((source, index) => (
                      <div key={index} className={`text-sm border ${colors.borderColor} rounded-md ${isMobile ? 'p-2' : 'p-3'} bg-white`}>
                        <div className="flex">
                          <div className={`mt-0.5 w-4 h-4 rounded-full ${colors.badgeBg} flex items-center justify-center ${colors.badgeText} ${isMobile ? 'mr-2' : 'mr-3'} flex-shrink-0`}>
                            <Check size={isMobile ? 8 : 10} />
                          </div>
                          <div>
                            <p className={`font-medium text-gray-800 ${isMobile ? 'text-xs mb-0.5' : 'mb-1'}`}>{source.name}</p>
                            {source.description && (
                              <p className={`text-gray-600 ${isMobile ? 'text-xs mb-1.5' : 'text-sm mb-2'}`}>{source.description}</p>
                            )}
                            {source.url && (
                              <a 
                                href={source.url} 
                                className={`${colors.linkText} flex items-center hover:underline ${isMobile ? 'text-xs' : 'text-sm'}`}
                                target="_blank" 
                                rel="noopener noreferrer"
                              >
                                {isMobile ? 'View Source' : 'View Original Source'} <ExternalLink size={isMobile ? 10 : 14} className="ml-1" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Video */}
                {embedUrl && (
                  <div>
                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Video Evidence</h4>
                    <div className="relative overflow-hidden pb-16:9 w-full" style={{ paddingBottom: '56.25%' }}> {/* 16:9 aspect ratio */}
                      <iframe
                        src={embedUrl}
                        title="Video content"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="absolute top-0 left-0 w-full h-full rounded-md shadow-md"
                      ></iframe>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Tags removed from here - will be added to footer */}
      
      {/* Citation moved to Source line in claim section */}
    </>
  );

  // When embedded in EnhancedQuoteCard, don't use CardContainer
  if (!standalone) {
    return (
      <div>
        {content}
      </div>
    );
  }
  
  // Use the CardContainer for standalone mode
  return (
    <CardContainer
      header={header}
      footer={footer}
      defaultExpanded={defaultExpanded}
    >
      {content}
    </CardContainer>
  );
};

export default ClaimRealityCard;